import { useState } from "react";
import { StarterPolicy } from "../types";
import { Badge } from "./Badges";
import { EnvData } from "../../../src/types";
import Button from "../../Button";
import WithIcon from "../../WithIcon";
import { HiArrowRight } from "react-icons/hi";
import FactView from "../../FactView";
import { factsEqual, useAddFact } from "../PolicyPage.utils";
import {
  EXAMPLE_ACTOR_ID,
  EXAMPLE_ROLE,
  EXAMPLE_RESOURCE_ID,
} from "../constants";

export const Facts: React.FC<{
  selectedEnv: EnvData;
  onBack: () => void;
  onComplete: () => void;
  state: StarterPolicy;
  dataComplete: boolean;
  backButton: React.ReactNode;
}> = ({ selectedEnv, onComplete, state, dataComplete, backButton }) => {
  const expectedActorId = EXAMPLE_ACTOR_ID;
  const expectedRole = EXAMPLE_ROLE;
  const expectedContainerId = EXAMPLE_RESOURCE_ID;

  const [actorId, setActorId] = useState<string>(
    dataComplete ? expectedActorId : ""
  );
  const [roleContainerId, setRoleContainerId] = useState<string>(
    dataComplete ? expectedContainerId : ""
  );
  const [role, setRole] = useState<string>(dataComplete ? expectedRole : "");
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  const { addFact, savingFact } = useAddFact(selectedEnv);

  const hasRoleFact = {
    predicate: "has_role",
    args: [
      { type: state.actor, id: actorId },
      { type: "String", id: role },
      { type: state.businessObject, id: roleContainerId },
    ],
  };

  const expectedHasRoleFact = {
    predicate: "has_role",
    args: [
      { type: state.actor, id: expectedActorId },
      { type: "String", id: expectedRole },
      { type: state.businessObject, id: expectedContainerId },
    ],
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (factsEqual(hasRoleFact, expectedHasRoleFact)) {
      addFact(hasRoleFact).then(() => {
        onComplete();
      });
    }
  };

  return (
    <div className="space-y-3 max-w-[900px]">
      <div>
        <div className="mb-3">
          Next, let&apos;s add the fact that <Badge>{state.actor}:alice</Badge>{" "}
          is a <Badge>viewer</Badge> on{" "}
          <Badge>{state.businessObject}:foo</Badge>:
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex flex-row flex-wrap space-x-2 items-center">
            <span>{state.actor} </span>
            <input
              placeholder={expectedActorId}
              value={actorId}
              onChange={(e) => setActorId(e.target.value)}
              required
              maxLength={10}
              className={`border mx-1 px-2 py-1 rounded flex-initial w-32 ${
                actorId !== "" && actorId !== expectedActorId
                  ? "border-red-600"
                  : ""
              }`}
            />
            <span>has the role </span>
            <select
              onChange={(e) => setRole(e.target.value)}
              className={`border mx-1 px-2 py-1 rounded w-24 ${
                role !== "" && role !== expectedRole ? "border-red-600" : ""
              }`}
              defaultValue={dataComplete ? expectedRole : ""}
              required
            >
              <option></option>
              <option>viewer</option>
              <option>owner</option>
            </select>
            <span>on {state.businessObject} </span>
            <input
              placeholder={expectedContainerId}
              value={roleContainerId}
              onChange={(e) => setRoleContainerId(e.target.value)}
              maxLength={10}
              className={`border mx-1 px-2 py-1 rounded flex-initial w-32 ${
                roleContainerId !== "" &&
                roleContainerId !== expectedContainerId
                  ? "border-red-600"
                  : ""
              }`}
              required
            />
            <span>.</span>
          </div>

          <div>
            <WithIcon icon={<HiArrowRight />}>
              <FactView fact={hasRoleFact} />
            </WithIcon>
          </div>
          <div className="flex flex-row justify-between">
            {hasSubmitted && !factsEqual(hasRoleFact, expectedHasRoleFact) && (
              <div className="text-red-800">
                {`Enter "${expectedActorId}", "${expectedRole}", and "${expectedContainerId}" to continue.`}
              </div>
            )}
            <div className="grow" />
          </div>
          <div className="flex flex-row justify-between">
            {backButton}
            <Button
              disabled={
                actorId === "" ||
                role === "" ||
                roleContainerId === "" ||
                savingFact
              }
              loading={savingFact}
              medium
              theme="primary"
              type="submit"
            >
              Add this Fact
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
